import {createContext, useContext, type ReactNode} from 'react';
import {useServiceStatusRead, type ServiceStatusState} from './useServiceStatus';

/**
 * One `GET /v1/location/service-status` per session, shared by everybody.
 *
 * Mounted at the root rather than by the screens that read it, because the
 * response answers two questions with different audiences: whether the venue is
 * serving (Q1, Q12, Q20) and **what timezone every timestamp on this surface
 * must render in** (Q14, Q17, Q9, Q20 — C-4, D-023). See the long comment in
 * `useServiceStatus.ts` for what the per-screen version got wrong.
 *
 * The context default is a dead state rather than `undefined`, so `useI18n`'s
 * "throw outside the provider" discipline is deliberately *not* copied here: a
 * screen rendered in a test harness without this provider should show no time,
 * which is the same thing it does when the server declines the question. There
 * is exactly one honest reading of "no zone" and both routes lead to it.
 */
const ServiceStatusContext = createContext<ServiceStatusState>({
  verdict: null,
  loading: false,
  notServing: false,
  /*
   * `true`, not `false`, and the difference matters. Outside the provider no
   * read is in flight and none is coming, so the question is as answered as it
   * will ever be — and the answer is the one this file already gives for a
   * server that declines: unknown, which is not closed. `false` here would mean
   * "still waiting" forever, and Q1 would sit on a scan that is never going to
   * resolve rather than carrying the guest to the menu.
   */
  settled: true,
  reload: () => {},
});

export function ServiceStatusProvider({children}: {children: ReactNode}) {
  const state = useServiceStatusRead();
  return (
    <ServiceStatusContext.Provider value={state}>{children}</ServiceStatusContext.Provider>
  );
}

/** Unchanged in name and shape from the per-screen hook it replaces. */
export function useServiceStatus(): ServiceStatusState {
  return useContext(ServiceStatusContext);
}
